import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/**
 * WaitingGame — Petal Bloom (calm, touch-first mini-game)
 * -------------------------------------------------------------
 * Tap petals to pop them (+score, streak).
 * Tap RARE gold petal (or 1% gopher) to vacuum all visible petals for a harvest bonus.
 * Smooth spawn/respawn, in-canvas HUD, adaptive performance, safe unmount.
 */

type WaitingGameProps = {
  title?: string;
  progress?: number | null;
  etaSeconds?: number | null;
  tips?: string[];
  autoClose?: boolean;
  onExit?: () => void;
  className?: string;
  labelColor?: string;
};

type PetalKind = "normal" | "gold" | "gopher";
type PetalState = "spawning" | "alive" | "dying" | "harvesting";

type Petal = {
  x: number; y: number;
  vx: number; vy: number;
  angle: number; spin: number;
  size: number;
  hue: number; sat: number; light: number;
  kind: PetalKind;
  state: PetalState;
  value: number;
  deathT: number;
  harvestAge: number;
  hx?: number; hy?: number;
  drift: number;
  spawnT?: number;
  targetX?: number;
  targetY?: number;
};

type Sparkle = { x: number; y: number; r: number; life: number; vx: number; vy: number; tint?: "gold" | "rose" };
type FloatLabel = { x: number; y: number; t: string; life: number };

const noop = (): void => undefined;

export default function WaitingGame({
  title = "Analyzing…",
  progress = null,
  etaSeconds = null,
  tips,
  autoClose = true,
  labelColor = "rgba(42,164,166,0.85)",
  onExit,
  className = "",
}: WaitingGameProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const runningRef = useRef(true);
  const mountedRef = useRef(true);
  const dprRef = useRef(1);

  const sizeRef = useRef({ w: 640, h: 360 });
  const petalsRef = useRef<Petal[]>([]);
  const sparklesRef = useRef<Sparkle[]>([]);
  const labelsRef = useRef<FloatLabel[]>([]);
  const lastTsRef = useRef(0);
  const nowRef = useRef(0);
  const bgShiftRef = useRef(0);

  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  const scoreRef = useRef(0);
  const streakRef = useRef(0);
  const streakTimerRef = useRef(0);

  // --- PERF: adaptive quality based on frame time ---
  const qualityRef = useRef(1);        // scales target petal count (0.6..1)
  const avgDtRef = useRef(1 / 60);     // EMA of dt
  const gameCallbacksRef = useRef({
    handleTap: (_x: number, _y: number) => { noop(); },
    draw: () => { noop(); },
    update: (_dt: number) => { noop(); },
    syncPetalCount: () => { noop(); },
  });

  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const MAX_SPARKLES = reducedMotion ? 200 : 500;
  const MAX_LABELS = 40;

  // Calm copy
  const lines = useMemo(
    () =>
      tips?.length
        ? tips
        : [
            "We’re carefully analyzing your product. Take a breath.",
            "Softly blooming… your results are on the way.",
            "Good things take a moment — thanks for your patience.",
            "Play with the petals — it’s your little zen garden.",
            "We’ll be done soon. Your skin will thank you.",
          ],
    [tips]
  );
  const [lineIndex, setLineIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => { setLineIndex((i) => (i + 1) % lines.length); }, 3800);
    return () => { clearInterval(id); };
  }, [lines.length]);

  // Responsive canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const resize = () => {
      if (!mountedRef.current) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;
      const w = Math.max(320, parent.clientWidth);
      const h = Math.max(220, Math.round(w * (9 / 16)));
      sizeRef.current = { w, h };
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      seedPetals(true);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);
    window.addEventListener("resize", resize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Input
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const getLocal = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!runningRef.current) return;
      const p = getLocal(e);
      pointerRef.current = p;
      gameCallbacksRef.current.handleTap(p.x, p.y);
      if ('vibrate' in navigator && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(10); } catch { /* vibration unsupported */ }
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!runningRef.current) return;
      pointerRef.current = getLocal(e);
    };
    const onPointerUp = () => { pointerRef.current = null; };
    canvas.addEventListener("pointerdown", onPointerDown, { passive: true });
    canvas.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", onPointerUp, { passive: true });

    const onVis = () => (runningRef.current = document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Loop
  useEffect(() => {
    mountedRef.current = true;
    runningRef.current = true;
    const step = (ts: number) => {
      if (!mountedRef.current) return;
      if (!runningRef.current || !canvasRef.current) {
        rafRef.current = requestAnimationFrame(step);
        return;
      }
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(0.033, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      // PERF: update EMA and adapt quality ~2Hz
      avgDtRef.current = avgDtRef.current * 0.92 + dt * 0.08;
      if ((ts % 500) < 16) {
        const targetFps = reducedMotion ? 50 : 58;
        const targetDt = 1 / targetFps;
        if (avgDtRef.current > targetDt + 0.003) {
          qualityRef.current = Math.max(0.6, qualityRef.current - 0.05);
          gameCallbacksRef.current.syncPetalCount();
        } else if (avgDtRef.current < targetDt - 0.003) {
          qualityRef.current = Math.min(1, qualityRef.current + 0.05);
          gameCallbacksRef.current.syncPetalCount();
        }
      }

      nowRef.current += dt;
      gameCallbacksRef.current.update(dt);
      gameCallbacksRef.current.draw();
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      mountedRef.current = false;
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [reducedMotion]);

  // Auto-close when progress finishes
  useEffect(() => {
    if (!autoClose || progress == null) return;
    if (progress >= 1) setTimeout(() => onExit?.(), 300);
  }, [progress, autoClose, onExit]);

  // ---------------- gameplay helpers ----------------
  function targetPetalBase() {
    const { w, h } = sizeRef.current;
    const base = Math.round(Math.max(18, Math.min(48, (w * h) / 13500)));
    return Math.max(10, base);
  }
  function currentTarget() {
    return Math.round(targetPetalBase() * qualityRef.current);
  }
  function syncPetalCount() {
    const arr = petalsRef.current;
    const tgt = currentTarget();
    while (arr.length < tgt) arr.push(makePetal(sizeRef.current.w, sizeRef.current.h, true));
    if (arr.length > tgt) arr.length = tgt;
  }
  function seedPetals(reset = false) {
    if (reset) petalsRef.current.length = 0;
    syncPetalCount();
  }

  function makePetal(w: number, h: number, spawn = true): Petal {
    let kind: PetalKind = "normal";
    if (Math.random() < 0.02) {
      kind = Math.random() < 0.01 ? "gopher" : "gold";
    }
    const size = rand(16, 28) * (Math.random() < 0.3 ? 1.25 : 1);
    const targetX = Math.random() * w;
    const targetY = Math.random() * h;

    // start near target (glide-in)
    const theta = Math.random() * Math.PI * 2;
    const r = rand(20, 80);
    const startX = clamp(targetX + Math.cos(theta) * r, -40, w + 40);
    const startY = clamp(targetY + Math.sin(theta) * r, -40, h + 40);

    let hue = randInt(170, 198), sat = rand(38, 60), light = rand(82, 93);
    if (kind !== "normal") { hue = 45; sat = 85; light = 60; }

    return {
      x: spawn ? startX : targetX,
      y: spawn ? startY : targetY,
      vx: rand(-12, 12),
      vy: rand(18, 26),
      angle: Math.random() * Math.PI * 2,
      spin: rand(-0.5, 0.5),
      size,
      hue, sat, light,
      kind,
      state: spawn ? "spawning" : "alive",
      value: kind === "normal" ? randInt(8, 14) : 0,
      deathT: 0,
      harvestAge: 0,
      hx: undefined, hy: undefined,
      drift: Math.random() * Math.PI * 2,
      spawnT: spawn ? 0 : 1,
      targetX, targetY,
    };
  }

  function handleTap(x: number, y: number) {
    const arr = petalsRef.current;
    for (let i = arr.length - 1; i >= 0; i--) {
      const p = arr[i];
      if (p.state !== "alive" && p.state !== "spawning") continue;
      const rad = p.size * (p.kind === "normal" ? 0.9 : 1.1);
      const d2 = (p.x - x) * (p.x - x) + (p.y - y) * (p.y - y);
      if (d2 <= rad * rad) {
        if (p.kind === "normal") {
          popPetal(i, true);
        } else {
          startHarvest(p.x, p.y, p.kind === "gopher");
          p.state = "harvesting";
          p.hx = p.x; p.hy = p.y;
        }
        return;
      }
    }
  }

  function popPetal(index: number, applyStreak: boolean) {
    const p = petalsRef.current[index];
    if (p.state === "dying") return;
    p.state = "dying";
    p.deathT = 0;

    let base = p.value || 10;
    if (applyStreak) {
      streakRef.current += 1;
      streakTimerRef.current = 3.5;
      base += Math.floor(streakRef.current / 4) * 2;
    }
    addScore(base, p.x, p.y);

    const burst = reducedMotion ? 6 : 10;
    for (let s = 0; s < burst; s++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 50 + Math.random() * 80;
      pushSparkle({ x: p.x, y: p.y, r: 1.2 + Math.random() * 2.2, life: 1, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, tint: "rose" });
    }
  }

  function startHarvest(cx: number, cy: number, gopher: boolean) {
    for (const p of petalsRef.current) {
      if (p.kind === "normal" && p.state === "alive") {
        p.state = "harvesting";
        p.harvestAge = 0;
        p.hx = cx; p.hy = cy;
      }
    }
    const ring = reducedMotion ? 18 : 30;
    for (let i = 0; i < ring; i++) {
      const a = (i / ring) * Math.PI * 2;
      const sp = 90 + Math.random() * 60;
      pushSparkle({ x: cx, y: cy, r: 1.4 + Math.random() * 2.4, life: 1, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, tint: "gold" });
    }
    labelsRef.current.push({ x: cx, y: cy - 10, t: gopher ? "Gopher Harvest!" : "Golden Harvest!", life: 1.2 });
    trimLabels();
  }

  function addScore(pts: number, x: number, y: number) {
    scoreRef.current += pts;
    labelsRef.current.push({ x, y, t: `+${pts}`, life: 1 });
    trimLabels();
  }

  function pushSparkle(s: Sparkle) {
    sparklesRef.current.push(s);
    if (sparklesRef.current.length > MAX_SPARKLES) {
      sparklesRef.current.splice(0, sparklesRef.current.length - MAX_SPARKLES);
    }
  }
  function trimLabels() {
    if (labelsRef.current.length > MAX_LABELS) {
      labelsRef.current.splice(0, labelsRef.current.length - MAX_LABELS);
    }
  }

  // ---------------- simulation ----------------
  function update(dt: number) {
    const { w, h } = sizeRef.current;
    bgShiftRef.current += dt * 4;

    if (streakRef.current > 0) {
      streakTimerRef.current -= dt;
      if (streakTimerRef.current <= 0) streakRef.current = 0;
    }

    for (let i = sparklesRef.current.length - 1; i >= 0; i--) {
      const s = sparklesRef.current[i];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vx *= 0.985;
      s.vy = s.vy * 0.985 + 18 * dt;
      s.life -= dt * 0.8;
      if (s.life <= 0) sparklesRef.current.splice(i, 1);
    }

    for (let i = labelsRef.current.length - 1; i >= 0; i--) {
      const f = labelsRef.current[i];
      f.y -= 22 * dt;
      f.life -= dt * 0.6;
      if (f.life <= 0) labelsRef.current.splice(i, 1);
    }

    const ptr = pointerRef.current;
    let harvestingNormals = 0;
    let goldCenter: { x: number; y: number } | null = null;

    for (let i = petalsRef.current.length - 1; i >= 0; i--) {
      const p = petalsRef.current[i];

      if (p.state === "spawning") {
        p.spawnT = Math.min(1, (p.spawnT ?? 0) + dt * 1.8);
        const tx = p.targetX ?? p.x;
        const ty = p.targetY ?? p.y;
        p.x = lerp(p.x, tx, 0.22);
        p.y = lerp(p.y, ty, 0.22);
        p.vx = lerp(p.vx, 0, 0.2);
        p.vy = lerp(p.vy, 18, 0.1);
        if (p.spawnT >= 1) { p.state = "alive"; p.spawnT = 1; }
        continue;
      }

      if ((p.kind === "gold" || p.kind === "gopher") && (p.state === "alive" || p.state === "harvesting")) {
        goldCenter = { x: p.x, y: p.y };
      }

      if (p.state === "harvesting" && p.kind === "normal" && p.hx != null && p.hy != null) {
        harvestingNormals++;
        p.harvestAge += dt;
        const fac = clamp(0.28 + 0.25 * (1 - Math.pow(1 - p.harvestAge / 0.9, 3)), 0.28, 0.9);
        p.x = lerp(p.x, p.hx, fac);
        p.y = lerp(p.y, p.hy, fac);
        p.size = lerp(p.size, 2, 0.25);
        p.angle += 2.4 * dt;

        const dist = Math.hypot((p.hx - p.x), (p.hy - p.y));
        if (dist < 8 || p.harvestAge >= 1.2) {
          addScore(p.value || 10, p.hx, p.hy);
          const burst = reducedMotion ? 4 : 6;
          for (let s = 0; s < burst; s++) {
            const a = Math.random() * Math.PI * 2;
            const sp = 60 + Math.random() * 40;
            pushSparkle({ x: p.hx, y: p.hy, r: 1 + Math.random() * 2, life: 0.9, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, tint: "gold" });
          }
          petalsRef.current.splice(i, 1);
          petalsRef.current.push(makePetal(w, h, true));
          continue;
        }
        continue;
      }

      if ((p.kind === "gold" || p.kind === "gopher") && p.state === "harvesting") {
        p.angle += (p.kind === "gopher" ? 0.6 : 1.2) * dt;
      }

      if (p.state === "dying") {
        p.deathT = Math.min(1, p.deathT + dt * 2);
        if (p.deathT >= 1) {
          petalsRef.current.splice(i, 1);
          petalsRef.current.push(makePetal(w, h, true));
          continue;
        }
        continue;
      }

      // alive
      p.drift += dt * 0.8;
      const sway = Math.sin(p.drift + i * 0.6) * 5.2;
      p.vx += sway * dt;
      p.vy = lerp(p.vy, 20 + Math.sin(p.drift * 0.8) * 3.2, 0.02);

      if (ptr) {
        const dx = ptr.x - p.x;
        const dy = ptr.y - p.y;
        const d = Math.hypot(dx, dy) || 1;
        if (d < 180) {
          const pull = (1 - d / 180) * 12;
          p.vx += (dx / d) * pull * dt;
          p.vy += (dy / d) * pull * dt * 0.7;
        }
      }

      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.angle += p.spin * dt;

      if (p.y - p.size > h + 40) {
        petalsRef.current.splice(i, 1);
        petalsRef.current.push(makePetal(w, h, true));
        continue;
      }
      if (p.x < -40) p.x = w + 40;
      if (p.x > w + 40) p.x = -40;
    }

    if (goldCenter && harvestingNormals === 0) {
      for (let i = petalsRef.current.length - 1; i >= 0; i--) {
        const p = petalsRef.current[i];
        if ((p.kind === "gold" || p.kind === "gopher") && p.state === "harvesting") {
          addScore(30, p.x, p.y);
          const finale = reducedMotion ? 12 : 24;
          for (let s = 0; s < finale; s++) {
            const a = Math.random() * Math.PI * 2;
            const sp = 120 + Math.random() * 120;
            pushSparkle({ x: p.x, y: p.y, r: 1.5 + Math.random() * 2.8, life: 1.2, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, tint: "gold" });
          }
          petalsRef.current.splice(i, 1);
          petalsRef.current.push(makePetal(sizeRef.current.w, sizeRef.current.h, true));
          break;
        }
      }
    }
  }

  // ---------------- rendering ----------------
  function draw() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { w, h } = sizeRef.current;
    const dpr = dprRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const t = bgShiftRef.current;
    const topHue = 178 + Math.sin(t * 0.05) * 8;
    const botHue = 266 + Math.cos(t * 0.04) * 6;
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, `hsl(${topHue}, 68%, 96%)`);
    g.addColorStop(1, `hsl(${botHue}, 74%, 98%)`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // soft beams
    for (let i = 0; i < 3; i++) {
      const y = ((t * 12 + i * 220) % (h + 300)) - 150;
      const grad = ctx.createRadialGradient(w * (0.2 + 0.3 * i), y, 10, w * (0.2 + 0.3 * i), y, 280);
      grad.addColorStop(0, "rgba(255,255,255,0.22)");
      grad.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(w * (0.2 + 0.3 * i), y, 280, 0, Math.PI * 2);
      ctx.fill();
    }

    // normals first, specials on top
    for (const p of petalsRef.current) if (p.kind === "normal") drawPetal(ctx, p);
    for (const p of petalsRef.current) if (p.kind !== "normal") drawSpecial(ctx, p);

    // sparkles
    for (const s of sparklesRef.current) {
      const a = Math.max(0, Math.min(1, s.life));
      ctx.fillStyle = s.tint === "gold" ? `rgba(255,215,0,${0.85 * a})` : s.tint === "rose" ? `rgba(42,164,166,${0.7 * a})` : `rgba(255,255,255,${0.85 * a})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    // labels
    ctx.font = "600 12px ui-sans-serif, -apple-system, Segoe UI, Inter, Roboto, system-ui";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const f of labelsRef.current) {
      ctx.fillStyle = `rgba(0,0,0,${0.18 * f.life})`;
      ctx.fillText(f.t, f.x + 1, f.y + 1);
      ctx.fillStyle = labelColor;
      ctx.fillText(f.t, f.x, f.y);
    }

    // pointer glow (safe radius)
    const ptr = pointerRef.current;
    if (ptr) {
      const R = safeRad(60);
      const gl = ctx.createRadialGradient(ptr.x, ptr.y, 0, ptr.x, ptr.y, R);
      gl.addColorStop(0, "rgba(255,255,255,0.24)");
      gl.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = gl;
      ctx.beginPath();
      ctx.arc(ptr.x, ptr.y, R, 0, Math.PI * 2);
      ctx.fill();
    }

    drawHUD(ctx, w, h);
  }

  function drawHUD(ctx: CanvasRenderingContext2D, w: number, _h: number) {
    const pad = 10, boxW = 148, boxH = 52, x = w - boxW - pad, y = pad;
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.82)";
    roundRect(ctx, x, y, boxW, boxH, 10); ctx.fill();
    ctx.fillStyle = "rgba(17,24,39,0.9)";
    ctx.font = "700 14px ui-sans-serif, Inter, system-ui";
    ctx.textAlign = "left"; ctx.textBaseline = "top";
    ctx.fillText(`Score ${scoreRef.current}`, x + 12, y + 10);
    ctx.font = "600 12px ui-sans-serif, Inter, system-ui";
    const streak = streakRef.current;
    ctx.fillText(streak > 0 ? `Streak x${streak}` : "Streak x0", x + 12, y + 28);
    ctx.restore();
  }

  function drawPetal(ctx: CanvasRenderingContext2D, p: Petal) {
    const { x, y, angle, size, hue, sat, light, state, deathT } = p;
    ctx.save();
    ctx.translate(x, y);

    let s = Math.max(0.1, size);
    let rot = angle;
    let alpha = 1;

    if (state === "spawning") {
      const t = easeOutBack(Math.min(1, p.spawnT ?? 0));
      s = size * (0.7 + 0.35 * t);
      alpha = 0.2 + 0.8 * t;
      rot = angle + (1 - t) * 0.4;
    } else if (state === "dying") {
      const t = easeOutCubic(Math.min(1, deathT));
      s = size * (1 - 0.85 * t);
      alpha = 1 - 0.7 * t;
      rot = angle + t * 1.6;
    } else if (state === "harvesting") {
      alpha = 0.9;
    }

    ctx.globalAlpha = alpha;
    ctx.rotate(rot);
    ctx.shadowColor = `hsla(${hue}, ${sat}%, ${Math.min(98, light + 8)}%, 0.5)`;
    ctx.shadowBlur = 16;

    const ww = s * 0.9, hh = s * 1.5;
    ctx.beginPath();
    ctx.moveTo(0, -hh * 0.5);
    ctx.bezierCurveTo(ww * 0.7, -hh * 0.3, ww * 0.7, hh * 0.2, 0, hh * 0.55);
    ctx.bezierCurveTo(-ww * 0.7, hh * 0.2, -ww * 0.7, -hh * 0.3, 0, -hh * 0.5);

    const fillGrad = ctx.createLinearGradient(0, -hh * 0.6, 0, hh * 0.7);
    fillGrad.addColorStop(0, `hsla(${hue}, ${sat + 10}%, ${light}%, 0.97)`);
    fillGrad.addColorStop(1, `hsla(${hue - 10}, ${Math.max(40, sat - 20)}%, ${Math.max(70, light - 14)}%, 0.92)`);
    ctx.fillStyle = fillGrad;
    ctx.fill();

    ctx.strokeStyle = `hsla(${hue - 10}, 30%, 60%, 0.28)`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, -hh * 0.4);
    ctx.quadraticCurveTo(0, 0, 0, hh * 0.45);
    ctx.stroke();

    ctx.restore();
  }

  function drawSpecial(ctx: CanvasRenderingContext2D, p: Petal) {
    if (p.kind === "gold") drawGoldPetal(ctx, p);
    else drawGopher(ctx, p);
  }

  function drawGoldPetal(ctx: CanvasRenderingContext2D, p: Petal) {
    const { x, y, angle, size, state, deathT } = p;
    ctx.save();
    ctx.translate(x, y);
    const pulse = 0.06 * Math.sin(nowRef.current * 3) + 1;
    let s = Math.max(0.1, size * 1.15 * pulse);
    let alpha = 1;
    let rot = angle;

    if (state === "spawning") {
      const t = easeOutBack(Math.min(1, p.spawnT ?? 0));
      s *= 0.7 + 0.35 * t;
      alpha = 0.2 + 0.8 * t;
      rot += (1 - t) * 0.3;
    } else if (state === "dying") {
      const t = easeOutCubic(Math.min(1, deathT));
      s *= Math.max(0.15, 1 - 0.85 * t);
      alpha = 1 - 0.7 * t;
      rot += t * 1.4;
    }

    ctx.globalAlpha = alpha;
    ctx.rotate(rot);

    // SAFE gradient radius
    const R = safeRad(s * 1.2);
    const gl = ctx.createRadialGradient(0, 0, 0, 0, 0, R);
    gl.addColorStop(0, "rgba(255,223,87,0.65)");
    gl.addColorStop(1, "rgba(255,223,87,0)");
    ctx.fillStyle = gl;
    ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.fill();

    const ww = s * 0.95, hh = s * 1.55;
    ctx.beginPath();
    ctx.moveTo(0, -hh * 0.5);
    ctx.bezierCurveTo(ww * 0.7, -hh * 0.25, ww * 0.7, hh * 0.25, 0, hh * 0.55);
    ctx.bezierCurveTo(-ww * 0.7, hh * 0.25, -ww * 0.7, -hh * 0.25, 0, -hh * 0.5);

    const fill = ctx.createLinearGradient(0, -hh * 0.6, 0, hh * 0.7);
    fill.addColorStop(0, "rgba(255,215,0,0.98)");
    fill.addColorStop(1, "rgba(255,200,0,0.94)");
    ctx.fillStyle = fill;
    ctx.fill();

    ctx.lineWidth = 1.2;
    ctx.strokeStyle = "rgba(180,130,0,0.5)";
    ctx.stroke();

    ctx.restore();
  }

  function drawGopher(ctx: CanvasRenderingContext2D, p: Petal) {
    const { x, y, size, state, deathT } = p;
    ctx.save();
    ctx.translate(x, y);
    let s = Math.max(0.1, size * 1.1);
    let alpha = 1;
    if (state === "spawning") {
      const t = easeOutBack(Math.min(1, p.spawnT ?? 0));
      s *= 0.7 + 0.35 * t;
      alpha = 0.2 + 0.8 * t;
    } else if (state === "dying") {
      const t = easeOutCubic(Math.min(1, deathT));
      s *= Math.max(0.15, 1 - 0.85 * t);
      alpha = 1 - 0.7 * t;
    }
    ctx.globalAlpha = alpha;

    const R = safeRad(s * 1.1);
    const gl = ctx.createRadialGradient(0, 0, 0, 0, 0, R);
    gl.addColorStop(0, "rgba(255,223,87,0.55)");
    gl.addColorStop(1, "rgba(255,223,87,0)");
    ctx.fillStyle = gl;
    ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.fill();

    ctx.fillStyle = "#9ac7e1";
    roundRect(ctx, -s * 0.45, -s * 0.4, s * 0.9, s * 0.95, s * 0.22);
    ctx.fill();
    ctx.fillStyle = "#7fb2d2";
    roundRect(ctx, -s * 0.44, -s * 0.48, s * 0.25, s * 0.22, s * 0.1);
    roundRect(ctx,  s * 0.19, -s * 0.48, s * 0.25, s * 0.22, s * 0.1);

    ctx.fillStyle = "#000";
    ctx.beginPath(); ctx.arc(-s * 0.18, -s * 0.06, s * 0.07, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc( s * 0.18, -s * 0.06, s * 0.07, 0, Math.PI * 2); ctx.fill();

    ctx.fillStyle = "#fff";
    roundRect(ctx, -s * 0.12, s * 0.22, s * 0.1, s * 0.2, s * 0.02);
    roundRect(ctx,  s * 0.02,  s * 0.22, s * 0.1, s * 0.2, s * 0.02);

    ctx.restore();
  }

  // Body scroll lock while game is visible
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  // --------------- UI chrome ---------------
  const etaLabel =
    typeof etaSeconds === "number" && etaSeconds >= 0
      ? `~${Math.max(0, Math.ceil(etaSeconds))}s left`
      : "Relax while we finish up.";

  gameCallbacksRef.current = { handleTap, draw, update, syncPetalCount };

  return ReactDOM.createPortal(
    <motion.div
      key="petal-bloom-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Calming loading game"
    >
      {/* Full-viewport backdrop */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(31,36,33,0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }} />

      {/* Game card */}
      <motion.div
        initial={{ y: 20, scale: 0.97, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 420, damping: 38, mass: 0.85 }}
        className={`relative w-full overflow-hidden ${className}`}
        style={{
          maxWidth: 520,
          borderRadius: 'var(--r-xl)',
          border: '1px solid var(--rule)',
          background: 'var(--c-cream)',
          boxShadow: 'var(--sh-lifted)',
          maxHeight: '90dvh',
          overflowY: 'auto',
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            borderBottom: '1px solid var(--rule)',
            background: 'rgba(248,244,238,0.88)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{title}</div>
            <div className="text-[11px]" style={{ color: 'var(--ink-muted)' }}>{etaLabel}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block w-40"><ProgressBar progress={progress} /></div>
            {onExit && (
              <button onClick={onExit} className="btn btn--ghost btn--sm" style={{ fontSize: 11 }}>Minimize</button>
            )}
          </div>
        </div>

        <div
          className="relative"
          style={{ background: 'linear-gradient(to bottom, color-mix(in srgb, var(--c-kivo-teal) 7%, var(--c-cream)), var(--c-sand))' }}
        >
          <canvas ref={canvasRef} className="block w-full h-auto touch-none select-none" />
          <div className="sm:hidden absolute left-0 right-0 bottom-0 p-3">
            <ProgressBar progress={progress} />
          </div>
        </div>

        <div className="flex items-center px-4 py-2" style={{ background: 'var(--c-cream)' }}>
          {/* Reserve room for two lines so the card height stays stable as tips
              rotate, and wrap (clamped to 2 lines) instead of hard-truncating. */}
          <div
            className="text-[11px] min-w-0 flex items-center"
            style={{ color: 'var(--ink-muted)', minHeight: '2.8em', width: '100%' }}
          >
            <AnimatePresence mode="wait">
              <motion.span
                key={lineIndex}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18 }}
                className="block"
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  lineHeight: 1.4,
                }}
              >
                {lines[lineIndex]}
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

/* ---------- Small UI bits ---------- */
function ProgressBar({ progress }: { progress: number | null | undefined }) {
  if (progress == null) {
    return (
      <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--rule)' }}>
        <motion.div
          className="h-full w-1/3 rounded-full"
          style={{ background: 'var(--c-kivo-teal)' }}
          animate={{ x: ["-120%", "150%"] }}
          transition={{ repeat: Infinity, duration: 1.25, ease: "easeInOut" }}
        />
      </div>
    );
  }
  const pct = Math.max(0, Math.min(1, progress));
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'var(--rule)' }}>
      <motion.div
        className="h-full rounded-full"
        style={{ width: `${pct * 100}%`, background: 'var(--c-deep-teal)' }}
        initial={false}
        animate={{ width: `${pct * 100}%` }}
        transition={{ type: "spring", stiffness: 220, damping: 26, mass: 0.4 }}
      />
    </div>
  );
}

/* ---------- Utils ---------- */
function rand(a: number, b: number) { return a + Math.random() * (b - a); }
function randInt(a: number, b: number) { return Math.floor(rand(a, b + 1)); }
function clamp(v: number, a: number, b: number) { return Math.max(a, Math.min(b, v)); }
function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function easeOutCubic(t: number) { return 1 - Math.pow(1 - t, 3); }
function easeOutBack(t: number, s = 1.70158) { return 1 + s * Math.pow(t - 1, 3) + s * (t - 1) * Math.pow(t - 1, 2); }
function safeRad(r: number) {
  // Guard against negatives/NaN/zero and excessively huge radii.
  if (!Number.isFinite(r) || r <= 0) return 0.5;
  return Math.min(4096, r);
}
